import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, useToast } from '@rb/ui'
import { getCurrentUser, setCurrentUser, clearCurrentUser } from '../lib/auth'
import { apiFetch } from '../utils/api'
import styles from './Settings.module.css'

function getTheme(): 'dark' | 'light' {
  return (document.documentElement.dataset.theme as 'dark' | 'light') ?? 'dark'
}

function applyTheme(t: 'dark' | 'light') {
  document.documentElement.dataset.theme = t
  localStorage.setItem('rb_theme', t)
}

export default function Settings() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const user = getCurrentUser()

  // ── Name ──────────────────────────────────────────────────────────────────
  const [name, setName] = useState(user?.name ?? '')
  const [nameSaving, setNameSaving] = useState(false)

  async function saveName(e: React.FormEvent) {
    e.preventDefault()
    setNameSaving(true)
    try {
      const data = await apiFetch<{ ok: boolean; name: string }>('/settings/name', {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      })
      if (user) setCurrentUser({ ...user, name: data.name })
      showToast('Name gespeichert', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Fehler beim Speichern', 'error')
    } finally {
      setNameSaving(false)
    }
  }

  // ── Password ───────────────────────────────────────────────────────────────
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwError, setPwError] = useState('')

  async function savePassword(e: React.FormEvent) {
    e.preventDefault()
    setPwError('')
    if (newPw !== confirmPw) {
      setPwError('Passwörter stimmen nicht überein')
      return
    }
    if (newPw.length < 8) {
      setPwError('Mindestens 8 Zeichen erforderlich')
      return
    }
    setPwSaving(true)
    try {
      await apiFetch('/settings/password', {
        method: 'PATCH',
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      })
      setCurrentPw('')
      setNewPw('')
      setConfirmPw('')
      showToast('Passwort geändert', 'success')
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Fehler beim Ändern des Passworts')
    } finally {
      setPwSaving(false)
    }
  }

  // ── Email ──────────────────────────────────────────────────────────────────
  const [newEmail, setNewEmail] = useState('')
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailPending, setEmailPending] = useState<string | null>(null)

  async function requestEmailChange(e: React.FormEvent) {
    e.preventDefault()
    setEmailSaving(true)
    try {
      await apiFetch('/settings/email', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail }),
      })
      setEmailPending(newEmail)
      setNewEmail('')
      showToast('Bestätigungslink gesendet', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Fehler beim Ändern der E-Mail', 'error')
    } finally {
      setEmailSaving(false)
    }
  }

  // ── Theme ──────────────────────────────────────────────────────────────────
  const [theme, setTheme] = useState<'dark' | 'light'>(getTheme)

  function selectTheme(next: 'dark' | 'light') {
    applyTheme(next)
    setTheme(next)
  }

  // ── Logout ─────────────────────────────────────────────────────────────────
  function logout() {
    clearCurrentUser()
    navigate('/login')
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.heading}>Einstellungen</h1>
      </header>

      {/* Name */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Name</h2>
        <form onSubmit={saveName} className={styles.form}>
          <Input
            type="text"
            label="Anzeigename"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
          />
          <div className={styles.formActions}>
            <Button type="submit" variant="primary" disabled={nameSaving}>
              {nameSaving ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        </form>
      </section>

      {/* Email */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>E-Mail-Adresse</h2>
        <p className={styles.hint}>Aktuelle Adresse: <strong>{user?.email}</strong></p>
        {emailPending && (
          <div className={styles.pendingNote}>
            Bestätigungslink an <strong>{emailPending}</strong> gesendet — Ihre bisherige Adresse
            bleibt aktiv, bis Sie bestätigen.
          </div>
        )}
        <form onSubmit={requestEmailChange} className={styles.form}>
          <Input
            type="email"
            label="Neue E-Mail-Adresse"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <div className={styles.formActions}>
            <Button type="submit" variant="primary" disabled={emailSaving}>
              {emailSaving ? 'Wird gesendet…' : 'Bestätigungslink senden'}
            </Button>
          </div>
        </form>
      </section>

      {/* Password */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Passwort ändern</h2>
        <form onSubmit={savePassword} className={styles.form}>
          <Input
            type="password"
            label="Aktuelles Passwort"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            autoComplete="current-password"
            required
          />
          <Input
            type="password"
            label="Neues Passwort"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            autoComplete="new-password"
            required
          />
          <Input
            type="password"
            label="Neues Passwort bestätigen"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
            required
          />
          {pwError && <p className={styles.error}>{pwError}</p>}
          <div className={styles.formActions}>
            <Button type="submit" variant="primary" disabled={pwSaving}>
              {pwSaving ? 'Wird geändert…' : 'Passwort ändern'}
            </Button>
          </div>
        </form>
      </section>

      {/* Theme */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Darstellungsmodus</h2>
        <div className={styles.radioList} role="radiogroup" aria-label="Darstellungsmodus">
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="theme"
              value="dark"
              checked={theme === 'dark'}
              onChange={() => selectTheme('dark')}
            />
            <span className={styles.radioLabel}>Dunkel</span>
          </label>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="theme"
              value="light"
              checked={theme === 'light'}
              onChange={() => selectTheme('light')}
            />
            <span className={styles.radioLabel}>Hell</span>
          </label>
        </div>
      </section>

      {/* Logout */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Abmelden</h2>
        <p className={styles.hint}>Sie werden abgemeldet und zur Anmeldeseite weitergeleitet.</p>
        <Button variant="destructive" onClick={logout}>
          Abmelden
        </Button>
      </section>
    </div>
  )
}
